import { AllocatedCustomerOrder, FormattedCustomerOrder } from "../common/CustomerOrder";
import { Receive } from "../common/Receive";
import { getTimestampInLocalTime } from "./dateUtil";

/*
    Return each line item from the receive with a new quantityAvailable field.
    quantityAvailable = receivedQuantity - quantityAlreadyReceived 
    quantityAlreadyReceived tracks how much stock has already been finalised.
    receivedQuantity is the total quantity (previous + new).
    The scenario is when we have part received and raised credit requests for the missing stock.
    Then the remaining stock is received and finalised. 
 */
const receiveLineItemQuantityBeingReceived = (
	receive: Receive,
	originalReceive?: Receive
): {
	sku: string;
	description: string;
	quantityAvailable: number;
}[] => {
	return receive.receiveLineItems.map((ri) => {
		let quantityAlreadyReceived = ri.quantityAlreadyReceived;
		if (originalReceive) {
			quantityAlreadyReceived =
				originalReceive.receiveLineItems.find((li) => li.sku === ri.sku)?.quantityAlreadyReceived ?? 0;
		}

		return {
			sku: ri.sku,
			description: ri.description,
			quantityAvailable: ri.receivedQuantity - quantityAlreadyReceived,
		};
	});
};

export function reduceCustomerOrdersToFIFO(
	receive: Receive,
	customerOrders: FormattedCustomerOrder[],
	originalReceive?: Receive
): AllocatedCustomerOrder[] {
	try {
		const receiveQuantities: { sku: string; description: string; quantityAvailable: number }[] =
			receiveLineItemQuantityBeingReceived(receive, originalReceive);
		const sortedCustomerOrders = customerOrders.sort(
			(a, b) => new Date(a.sortTimestamp).getTime() - new Date(b.sortTimestamp).getTime()
		);
		const allocatedCustomerOrders: AllocatedCustomerOrder[] = [];
		sortedCustomerOrders.forEach((c) => {
			let receiveQuantity = receiveQuantities.find((rli) => rli.sku === c.sku);
			if (!receiveQuantity) return;
			const unallocatedOrderQuantity = c.quantity - c.quantityAllocated;
			const allocatedQuantity = Math.min(receiveQuantity.quantityAvailable, unallocatedOrderQuantity);
			if (allocatedQuantity > 0) {
				allocatedCustomerOrders.push({
					OrderId: c.orderId,
					OrderNumber: c.orderNumber,
					Sku: c.sku,
					Description: receiveQuantity.description,
					System: c.source,
					OrderDate: c.dateCreated,
					LineItemId: c.lineItemId,
					Quantity: c.quantity,
					QuantityAllocated: c.quantityAllocated,
					QuantityToAllocate: c.quantityAllocated + allocatedQuantity,
					PONumber: receive.purchaseOrderNumber,
					InvoiceNumber: receive.invoiceNumber,
					ReceiveDate: getTimestampInLocalTime(receive.receivedDate, "dd/MM/yy"),
					QuantityReceived: receiveQuantity.quantityAvailable,
					FulfilmentType: c.fulfilmentType,
					IsAllocated: true,
					Location: receive.locationId,
					CustomerPhone: c.customerPhone,
					CustomerName: c.customerName,
					CustomerId: c.customerId,
					Status: c.status,
				});
				receiveQuantity.quantityAvailable -= allocatedQuantity;
			}
		});
		return allocatedCustomerOrders;
	} catch (error) {
		console.log(error);
		return [];
	}
}

export function getAvailabileForCustomerOrderQty(
	invoicedQuantity: number,
	originalReceivedQuantity: number,
	customerOrderQuantity?: number
): number {
	if (customerOrderQuantity && customerOrderQuantity > 0) {
		const invoiceVsOriginalReceived = invoicedQuantity - originalReceivedQuantity;
		const currentReceiveQtyVsCustomerOrder = invoiceVsOriginalReceived - customerOrderQuantity;
		if (currentReceiveQtyVsCustomerOrder > 0) {
			return customerOrderQuantity;
		}
		return invoiceVsOriginalReceived;
	}
	return 0;
}
